import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import DashboardSidebar from '../components/dashboardPage_components/DashboardSidebar';
import DashboardContent from '../components/dashboardPage_components/DashboardContent';
import { SectionType } from '../components/dashboardPage_components/dashboard.types';
import styles from '../components/dashboardPage_components/Dashboard.module.css';

/**
 * Main Dashboard Component
 * 
 * This is the primary dashboard page component that manages the overall layout
 * and state for the dashboard application. It contains the sidebar navigation
 * and main content area.
 */
const Dashboard = () => {
  // State to track which section is currently active
  const [activeSection, setActiveSection] = useState('overview' as SectionType);
  
  // Mobile sidebar visibility state
  const [isMobileSidebarVisible, setIsMobileSidebarVisible] = useState(false);
  
  // Screen size state for responsive design
  const [screenSize, setScreenSize] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  // Router location to read query params (e.g., ?tab=referrals)
  const location = useLocation();

  // Handle screen size detection
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 576) {
        setScreenSize('mobile');
      } else if (width <= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('desktop');
      }
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sync "tab" query parameter to the active dashboard section
  useEffect(() => {
    // Parse the current query string
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');

    // Define allowed section names to validate the param
    const allowedSections: SectionType[] = ['overview', 'history', 'plans', 'settings', 'referrals', 'affiliate'];

    if (tabParam && (allowedSections as string[]).includes(tabParam)) {
      // Cast is safe after validation
      setActiveSection(tabParam as SectionType);
    }
  }, [location.search]);

  /**
   * Handle section changes from navigation
   * @param sectionName - The name of the section to activate
   */
  const handleSectionChange = (sectionName: SectionType): void => {
    setActiveSection(sectionName);
    // Close mobile sidebar when item is selected
    if (screenSize === 'mobile') {
      setIsMobileSidebarVisible(false);
    }
  };

  // Handle mobile sidebar toggle
  const handleMobileSidebarToggle = () => {
    setIsMobileSidebarVisible(!isMobileSidebarVisible);
  };

  // Handle overlay click to close mobile sidebar
  const handleOverlayClick = () => {
    setIsMobileSidebarVisible(false);
  };

  return (
    <div className={styles.dashboard}>
      {/* Mobile overlay */}
      {screenSize === 'mobile' && (
        <div
          className={`${styles.mobileOverlay} ${isMobileSidebarVisible ? styles.active : ''}`}
          onClick={handleOverlayClick}
        />
      )}

      {/* Sidebar component - contains navigation and logo */}
      <DashboardSidebar 
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        isMobileHidden={screenSize === 'mobile' && !isMobileSidebarVisible}
      />
      
      {/* Main content area - displays selected section content */}
      <DashboardContent 
        activeSection={activeSection}
        isMobileSidebarVisible={isMobileSidebarVisible}
        onMobileSidebarToggle={handleMobileSidebarToggle}
      />
    </div>
  );
};

export default Dashboard; 